import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { X, Download, Mail, MessageSquare, QrCode, Copy, Send, Link2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Interpolate {{placeholders}} in a marketing template body using clinic info.
 */
export function renderTemplateBody(body, clinic) {
  if (!body) return "";
  const map = {
    "{{clinic_name}}": clinic?.clinic_name || clinic?.name || "your clinic",
    "{{clinic_email}}": clinic?.email || "your clinic team",
    "{{clinic_contact}}": clinic?.contact_name || clinic?.clinic_name || "your front desk",
    "{{first_name}}": "[Patient first name]",
  };
  let out = body;
  for (const [k, v] of Object.entries(map)) {
    out = out.split(k).join(v);
  }
  return out;
}

const TEMPLATE_LABELS = {
  landing_page: "Landing page copy",
  email_sequence: "Email sequence",
  social_kit: "Social kit",
  pitch_deck: "Pitch deck outline",
  intake_form: "Intake form",
  one_pager: "One-pager",
};

export default function MarketingAssetModal({ asset, clinic, onClose }) {
  const [tab, setTab] = useState("preview"); // preview | deploy
  const [channel, setChannel] = useState("email");
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [linkedDeploymentId, setLinkedDeploymentId] = useState("");
  const [deployments, setDeployments] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/screener-deployments")
      .then((r) => setDeployments(r.data || []))
      .catch(() => setDeployments([]));
  }, []);

  const rendered = useMemo(() => renderTemplateBody(asset?.template_body, clinic), [asset, clinic]);
  const clinicName = clinic?.clinic_name || clinic?.name || "Your clinic";
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/marketing/${asset?.id || "asset"}`
      : `/marketing/${asset?.id || "asset"}`;

  if (!asset) return null;

  const _isMobile = () =>
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || "");

  const generatePdf = (download = true) => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 56;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;

    // Header strip
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 80, "F");
    doc.setTextColor(147, 197, 253);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("NODERA HEALTH · MARKETING ASSET", margin, 32);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text(clinicName.toUpperCase(), margin, 56);

    // Title block
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    const titleLines = doc.splitTextToSize(asset.title, usableWidth);
    doc.text(titleLines, margin, 120);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    const kindLabel = TEMPLATE_LABELS[asset.template_kind] || asset.kind || "Asset";
    doc.text(`${kindLabel}  ·  Customized for ${clinicName}`, margin, 120 + titleLines.length * 22 + 8);

    // Body text
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    const bodyStartY = 120 + titleLines.length * 22 + 38;
    const lines = doc.splitTextToSize(
      rendered || asset.description || "(No template body authored yet — ask your concierge to populate.)",
      usableWidth
    );
    let cursor = bodyStartY;
    const lineHeight = 15;
    for (const line of lines) {
      if (cursor > pageHeight - 80) {
        doc.addPage();
        cursor = margin;
      }
      doc.text(line, margin, cursor);
      cursor += lineHeight;
    }

    // Footer
    const footerY = pageHeight - 40;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, footerY - 12, pageWidth - margin, footerY - 12);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`${clinicName}  ·  ${clinic?.email || ""}`, margin, footerY);
    doc.text(`Powered by Nodera Health`, pageWidth - margin, footerY, { align: "right" });

    const safeName = (clinicName + "_" + asset.title).replace(/[^a-z0-9]+/gi, "_").slice(0, 60);
    const filename = `${safeName}.pdf`;

    // --- Desktop download: anchor-click with `download` attr works perfectly. ---
    if (download && !_isMobile()) {
      doc.save(filename);
      return;
    }

    // --- Mobile (download + preview), and desktop "Open in new tab": ---
    // The `download` attribute is silently ignored on iOS Safari and most
    // Android in-app browsers for blob URLs, which is why the original
    // doc.save() did nothing on mobile. Open the PDF as a blob in a new tab
    // so Safari's native PDF viewer + share sheet (Save to Files / Drive /
    // Print) takes over. Fall back to current-tab navigation if the popup is
    // blocked (common in in-app webviews).
    let blob;
    try {
      blob = doc.output("blob");
    } catch {
      // Older jsPDF / browser combos may need datauristring; final fallback below.
      blob = null;
    }

    if (blob && typeof URL?.createObjectURL === "function") {
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked → navigate the current tab so the user still gets the PDF.
        window.location.href = url;
      }
      // Give the new tab time to load before revoking.
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }, 60000);
      if (download && _isMobile()) {
        toast.success("PDF opened. Tap the share icon to save it to Files.");
      }
      return;
    }

    // Final fallback: data URI in an iframe (desktop "Open in new tab" legacy).
    // Build the iframe via DOM APIs instead of `document.write` to avoid the
    // (theoretical) XSS surface even though `dataUri` here is produced
    // exclusively by jsPDF and contains no user input. DOM mutation is also
    // friendlier to React StrictMode and Chrome's permission policies.
    const dataUri = doc.output("datauristring");
    const w = window.open();
    if (w) {
      const iframe = w.document.createElement("iframe");
      iframe.src = dataUri;
      iframe.style.border = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100vh";
      (w.document.body || w.document.documentElement).appendChild(iframe);
    } else if (download) {
      toast.error("Pop-up blocked. Allow pop-ups to download the PDF, or use the desktop site.");
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const submitDeploy = async () => {
    if (!channel) return;
    setSending(true);
    try {
      const r = await api.post("/marketing-assets/deploy", {
        asset_id: asset.id,
        channel,
        target: target || null,
        note: note || null,
        linked_deployment_id: linkedDeploymentId || null,
      });
      if (r.data?.deployed) {
        toast.success(`Deployed via ${channel}.`);
      } else {
        toast.success(
          `Deploy queued (${channel}). Email/SMS channels are LOG-ONLY until ops activates Resend/Twilio.`
        );
      }
      setTarget("");
      setNote("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="marketing-asset-modal"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-3xl h-full bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600">
              {TEMPLATE_LABELS[asset.template_kind] || asset.kind} · Customized for {clinicName}
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5 truncate">{asset.title}</h2>
          </div>
          <button
            type="button"
            data-testid="marketing-modal-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 rounded-full w-8 h-8 border border-slate-200 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="marketing-modal-tabs">
            <TabBtn id="preview" label="Preview" active={tab === "preview"} onClick={() => setTab("preview")} />
            <TabBtn id="deploy" label="Deploy" active={tab === "deploy"} onClick={() => setTab("deploy")} />
          </div>
        </div>

        {tab === "preview" && (
          <div className="p-6 space-y-4" data-testid="marketing-modal-preview">
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="marketing-modal-pdf-download"
                onClick={() => generatePdf(true)}
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </button>
              <button
                type="button"
                data-testid="marketing-modal-pdf-open"
                onClick={() => generatePdf(false)}
                className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
              >
                Open in new tab
              </button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="bg-slate-900 text-white px-6 py-5">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-blue-300">
                  Nodera Health · Marketing asset
                </div>
                <div className="font-display text-lg font-bold mt-1">{clinicName}</div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl font-extrabold tracking-tighter text-slate-900 leading-tight">
                  {asset.title}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {TEMPLATE_LABELS[asset.template_kind] || asset.kind} · Customized for {clinicName}
                </p>
                <pre className="mt-5 whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">
                  {rendered || asset.description || "(No template body authored yet — ask your concierge to populate.)"}
                </pre>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span>
                    {clinicName} · {clinic?.email || ""}
                  </span>
                  <span>Powered by Nodera Health</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "deploy" && (
          <div className="p-6 space-y-5" data-testid="marketing-modal-deploy">
            <div className="text-sm text-slate-600">
              Choose a channel and (optionally) a target. Email + SMS run in LOG-ONLY mode — admin will activate
              Resend/Twilio from the To Do list.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <ChannelBtn id="email" icon={Mail} label="Email" active={channel === "email"} onClick={() => setChannel("email")} />
              <ChannelBtn id="sms" icon={MessageSquare} label="SMS" active={channel === "sms"} onClick={() => setChannel("sms")} />
              <ChannelBtn id="qr" icon={QrCode} label="QR code" active={channel === "qr"} onClick={() => setChannel("qr")} />
              <ChannelBtn id="link" icon={Copy} label="Share link" active={channel === "link"} onClick={() => setChannel("link")} />
            </div>

            {channel === "email" && (
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="marketing-deploy-target-email"
                className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm"
              />
            )}
            {channel === "sms" && (
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="marketing-deploy-target-sms"
                className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm"
              />
            )}
            {channel === "link" && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-2">
                <code className="flex-1 text-xs text-slate-700 break-all">{shareUrl}</code>
                <button
                  onClick={copyShareLink}
                  data-testid="marketing-deploy-copy-link"
                  className="inline-flex items-center gap-1 text-xs font-semibold bg-white border border-slate-200 rounded-full px-3 py-1.5"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
            )}
            {channel === "qr" && (
              <div className="rounded-xl border border-slate-200 bg-white p-5 flex flex-col items-center gap-3">
                <QRCodeCanvas value={shareUrl} size={180} bgColor="#ffffff" fgColor="#0f172a" includeMargin />
                <code className="text-[11px] text-slate-500 break-all">{shareUrl}</code>
              </div>
            )}

            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Internal note (optional, logged with deploy)"
              data-testid="marketing-deploy-note"
              className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm resize-y"
            />

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
              <label className="block text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
                <Link2 className="w-3 h-3" /> Link to a screener deployment
                <span className="ml-1 font-normal text-slate-500">(optional, enables conversion tracking)</span>
              </label>
              <select
                data-testid="marketing-deploy-linked-deployment"
                value={linkedDeploymentId}
                onChange={(e) => setLinkedDeploymentId(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs"
              >
                <option value="">— Don't link to a screener —</option>
                {deployments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.custom_label || d.slug} · {d.submission_count || 0} subs
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500">
                Submissions on the linked screener will count as <strong>conversions</strong> for this asset in your
                Analytics tab.
              </p>
            </div>

            <button
              type="button"
              onClick={submitDeploy}
              disabled={sending}
              data-testid="marketing-deploy-submit"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5" /> {sending ? "Logging…" : "Deploy"}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

function TabBtn({ id, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`marketing-modal-tab-${id}`}
      onClick={onClick}
      className={`text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors ${
        active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
}

function ChannelBtn({ id, icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`marketing-deploy-channel-${id}`}
      onClick={onClick}
      className={`rounded-2xl border p-3 flex flex-col items-center gap-1 text-xs font-semibold transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
